@if ((shouldDisplayError$ | async) !== true && !hideHeader()) {
  <header class="top-bar">
    <div class="top-bar__container">
      <div class="top-bar__logo">
        @if (displayLogoUrl()) {
          <img [src]="displayLogoUrl()" alt="Business logo" />
        }
      </div>

      <div class="top-bar__heading">
        <h1 class="top-bar__name">{{ businessName() || (pageTitle | translate) }}</h1>
      </div>

      <div class="top-bar__right">
        @if (businessAddress()) {
          <button
            type="button"
            class="top-bar__location"
            [title]="businessAddress()"
            [attr.aria-expanded]="businessInfoOpen()"
            (click)="toggleBusinessInfo()"
          >
            <mat-icon class="top-bar__location-icon">location_on</mat-icon>
            <span class="top-bar__location-label">{{ businessAddress() }}</span>
          </button>
          @if (businessInfoOpen()) {
            <div class="business-info__backdrop" (click)="closeBusinessInfo()"></div>
            <div class="business-info" role="dialog" [attr.aria-label]="businessName() || businessAddress()">
              <div class="business-info__header">
                @if (businessName()) {
                  <h2 class="business-info__name">{{ businessName() }}</h2>
                }
                <button
                  type="button"
                  class="business-info__close"
                  i18n-aria-label="@@BOOK.CLOSE"
                  aria-label="Close"
                  (click)="closeBusinessInfo()"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
              <div class="business-info__row">
                <span class="business-info__row-icon-circle">
                  <mat-icon class="business-info__row-icon" fontSet="material-symbols-outlined">location_on</mat-icon>
                </span>
                <span class="business-info__row-text">
                  <span class="business-info__row-title">{{ businessAddressShort() }}</span>
                  @if (businessAddressRest()) {
                    <span class="business-info__row-subtitle">{{ businessAddressRest() }}</span>
                  }
                </span>
                <a
                  class="business-info__navigate"
                  [href]="businessMapsUrl()"
                  target="_blank"
                  rel="noopener"
                  i18n-aria-label="@@BOOK.OPEN_IN_GOOGLE_MAPS"
                  aria-label="Open in Google Maps"
                >
                  <mat-icon fontSet="material-symbols-outlined">near_me</mat-icon>
                </a>
              </div>
              @if (businessPhone()) {
                <div class="business-info__row">
                  <span class="business-info__row-icon-circle">
                    <mat-icon class="business-info__row-icon" fontSet="material-symbols-outlined">call</mat-icon>
                  </span>
                  <span class="business-info__row-text">
                    <span class="business-info__row-title" i18n="@@BOOK.CALL_US">Call us</span>
                    <span class="business-info__row-subtitle">{{ businessPhone() }}</span>
                  </span>
                </div>
              }
              @if (businessWebsite()) {
                <div class="business-info__row">
                  <span class="business-info__row-icon-circle">
                    <mat-icon class="business-info__row-icon" fontSet="material-symbols-outlined">language</mat-icon>
                  </span>
                  <span class="business-info__row-text">
                    <span class="business-info__row-title" i18n="@@BOOK.WEBSITE">Website</span>
                    <a
                      class="business-info__row-subtitle business-info__row-link"
                      [href]="businessWebsiteUrl()"
                      target="_blank"
                      rel="noopener"
                      >{{ businessWebsite() }}</a
                    >
                  </span>
                </div>
              }
            </div>
          }
        }
      </div>
    </div>
  </header>
}

<div class="layout-body" [class.layout-body--error]="(shouldDisplayError$ | async) === true">
  <div class="layout-body__container">
    @if (shouldDisplayError$ | async) {
      <!-- Error screens -->
      @switch (errorDisplay$ | async) {
        @case ('conflict-booking') {
          <app-booking-error
            i18n-title="@@BOOK.TIME_SLOT_UNAVAILABLE"
            title="Time slot unavailable"
            i18n-message="@@BOOK.TIME_SLOT_UNAVAILABLE_DESCRIPTION"
            message="The time slot you selected is no longer available. Please choose a new time to continue."
            i18n-buttonLabel="@@BOOK.SELECT_NEW_TIME"
            buttonLabel="Select new time"
            (buttonClick)="selectNewTime()"
          ></app-booking-error>
        }
        @case ('error-host') {
          <app-booking-error
            i18n-title="@@BOOK.HOST_NOT_FOUND"
            title="Host not found"
            i18n-message="@@BOOK.UNABLE_TO_DETERMINE_HOST"
            message="We cannot determine who the host of the meeting is supposed to be. Please try again."
          ></app-booking-error>
        }
        @case ('slug-mismatch-error') {
          <app-booking-error
            i18n-title="@@BOOK.SOMETHING_WENT_WRONG"
            title="Sorry, something went wrong!"
            i18n-message="@@GROUP_SERVICE.ERROR"
            message="There seems to be an issue with the URL. Please contact the administrator for assistance."
          ></app-booking-error>
        }
        @case ('error-lookup') {
          <app-booking-error
            i18n-title="@@BOOK.SOMETHING_WENT_WRONG"
            title="Sorry, something went wrong!"
            i18n-message="@@BOOK.PROBLEM_LOADING_MEETING"
            message="There was a problem loading the requested meeting."
          ></app-booking-error>
        }
        @case ('meeting-type-deleted') {
          <app-booking-error
            i18n-title="@@BOOK.EVENT_TYPE_IS_DELETED"
            title="Booking page is no longer available"
            i18n-message="@@BOOK.MEETING_TYPE_NO_LONGER_EXISTS"
            message="This meeting link is no longer available. Please contact your host."
          ></app-booking-error>
        }
        @case ('meeting-host-not-connected') {
          <app-booking-error
            i18n-title="@@BOOK.HOST_NOT_AVAILABLE"
            title="Selected host/calendar not connected"
            i18n-message="@@BOOK.HOST_NOT_AVAILABLE_ERROR_MESSAGE"
            message="We ran into an unexpected issue while scheduling your meeting/appointment and it has not been scheduled. Your host has been notified."
          ></app-booking-error>
        }
        @case ('meeting-type-expired') {
          <app-booking-error
            i18n-title="@@BOOK.EVENT_TYPE_IS_EXPIRED"
            title="There are no available date/time slots"
            i18n-message="@@BOOK.MEETING_TYPE_EXPIRED"
            message="Please get in touch with your host."
            i18n-buttonLabel="@@BOOK.SELECT_NEW_TIME"
            buttonLabel="Select new time"
            (buttonClick)="selectNewTime()"
          ></app-booking-error>
        }
        @case ('error-meeting-type') {
          <app-booking-error
            i18n-title="@@BOOK.EVENT_TYPE_ERROR"
            title="Issue loading booking page"
            i18n-message="@@BOOK.EVENT_TYPE_ERROR_DESCRIPTION"
            message="Something went wrong while setting up the meeting. Please contact your host."
          ></app-booking-error>
        }
        @case ('error-404') {
          <app-booking-error
            i18n-title="@@BOOK.EVENT_TYPE_NOT_FOUND"
            title="Booking page not found"
            i18n-message="@@BOOK.MEETING_TYPE_NOT_FOUND"
            message="The booking page you are looking for does not exist or has been removed."
          ></app-booking-error>
        }
        @case ('meeting-cancelled-cannot-reschedule') {
          <app-booking-error
            i18n-title="@@MEETING.CANCELLED_CANNOT_RESCHEDULE"
            title="Meeting has been cancelled"
            i18n-message="@@MEETING.CANCELLED_CANNOT_RESCHEDULE_DESCRIPTION"
            message="This meeting has been cancelled and can no longer be rescheduled."
          ></app-booking-error>
        }
        @case ('meeting-reschedule-not-found') {
          <app-booking-error
            i18n-title="@@MEETING.RESCHEDULE_NOT_FOUND"
            title="Meeting not found"
            i18n-message="@@MEETING.RESCHEDULE_NOT_FOUND_DESCRIPTION"
            message="The meeting you're trying to reschedule could not be found. Please check the URL or contact the host."
          ></app-booking-error>
        }
        @case ('meeting-reschedule-error') {
          <app-booking-error
            i18n-title="@@MEETING.RESCHEDULE_ERROR"
            title="Rescheduling failed"
            i18n-message="@@MEETING.RESCHEDULE_ERROR_DESCRIPTION"
            message="There was an error rescheduling this meeting. Please try again later or contact support."
          ></app-booking-error>
        }
        @default {
          <app-booking-error
            i18n-title="@@BOOK.SOMETHING_WENT_WRONG"
            title="Something went wrong"
            i18n-message="@@BOOK.UNEXPECTED_ERROR"
            message="An unexpected error occurred. Please try again later."
          ></app-booking-error>
        }
      }
    } @else {
      @if (!isRescheduleMode && currentStep() && providerStepDetermined()) {
        <nav class="breadcrumb">
          @for (step of steps(); track step.label; let last = $last) {
            @if (step.completed) {
              <button type="button" class="breadcrumb__step breadcrumb__step--completed" (click)="goToStep(step)">
                {{ step.label | translate }}
              </button>
            } @else {
              <span
                class="breadcrumb__step"
                [class.breadcrumb__step--active]="step.active"
                [class.breadcrumb__step--skipped]="step.skipped"
              >
                {{ step.label | translate }}
              </span>
            }
            @if (!last) {
              <span class="breadcrumb__separator">›</span>
            }
          }
        </nav>
      }

      @switch (currentStep()) {
        @case ('scheduling') {
          <app-scheduling-page></app-scheduling-page>
        }
        @case ('provider') {
          <app-provider-selection-page></app-provider-selection-page>
        }
        @case ('datetime') {
          <app-datetime-selection-page></app-datetime-selection-page>
        }
        @case ('confirm') {
          <app-confirm-booking-page></app-confirm-booking-page>
        }
      }
    }
  </div>
</div>
